import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  useContactCounts,
  UseContactCountsReturn,
} from 'src/pages/SharedPageCustomHooks/UseContactCounts/useContactCounts';
import {
  useDocumentsAPI,
  UseFaqsNotesDocumentsAPIReturn,
} from 'src/SharedCustomHooks/UseFaqsNotesDocumentsAPI/useDocumentsAPI';
import {
  FetchContactByIdResult,
  useContactAPI,
  UseContactAPIReturn,
} from 'src/SharedCustomHooks/UseContactAPI/useContactAPI';
import {
  useContactFormSubmissionContacts,
  UseContactFormSubmissionContactsReturn,
} from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/useContactFormSubmission';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';

const contactDocumentsAPIOptions = {
  customUploadErrorMessage:
    'Failed to upload this contact document. Please try again or contact Juniper support if the issue persists.',
};

export interface UseContactPageDataReturn {
  contactId: string;
  contact: IContact | null;
  contactRole: string | undefined;
  contactError: string | null;
  fetchContactById: (id: string) => Promise<void>;
  refetchSingleContactById: (id: string) => Promise<void>;

  contactCounts: any;
  countsError: string | null;

  documents: any[];
  notes: any[];
  loadingDocs: boolean;
  isSavingDoc: boolean;

  loadAllData: () => Promise<void>;
  refetchCounts: () => void;
  saveDocument: (
    document: DocumentFormData,
    entityId: string,
    userId: string
  ) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<boolean>;

  handleSubmitContactForm: (formData: IContactFormData) => Promise<void>;
  isFormLoading: boolean;
}

export const useContactPageData = (
  contactId: string
): UseContactPageDataReturn => {
  const user: any = useSelector(selectAuthenticatedUser);

  const contactAPI: UseContactAPIReturn = useContactAPI();
  const countsAPI: UseContactCountsReturn = useContactCounts();
  const documentsAPI: UseFaqsNotesDocumentsAPIReturn = useDocumentsAPI({
    ...contactDocumentsAPIOptions,
    user,
  });

  const [currentContact, setCurrentContact] = useState<IContact | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);

  const fetchContactById = async (id: string): Promise<void> => {
    if (!id || !user?.id) {
      setContactError('Contact ID or User ID is missing');
      return;
    }

    setContactError(null);

    const result: FetchContactByIdResult =
      await contactAPI.fetchContactById(id);

    if (result.ok && result.contact) {
      setCurrentContact(result.contact);
      return;
    }

    setCurrentContact(null);
    setContactError(`Contact with ID ${id} not found`);
  };

  const refetchSingleContactById = async (id: string): Promise<void> => {
    await fetchContactById(id);
  };

  const formAPI: UseContactFormSubmissionContactsReturn =
    useContactFormSubmissionContacts({
      onSuccess: () => refetchSingleContactById(contactId),
    });

  const contactRole: string | undefined =
    currentContact?.roles?.[0]?.role?.name;

  const refetchCounts = (): void => {
    if (!contactRole) {
      return;
    }

    void countsAPI.fetchContactCountsFromAPI(contactId, contactRole);
  };

  const loadAllData = async (): Promise<void> => {
    if (!contactRole) {
      return;
    }

    await Promise.all([
      countsAPI.fetchContactCountsFromAPI(contactId, contactRole),
      documentsAPI.fetchDocuments(contactId),
    ]);
  };

  const saveDocument = async (
    document: DocumentFormData,
    entityId: string,
    userId: string
  ): Promise<void> => {
    await documentsAPI.saveDocument(document, entityId, userId);
    refetchCounts();
  };

  const deleteDocument = async (documentId: string): Promise<boolean> => {
    const didDelete: boolean = await documentsAPI.deleteDocument(documentId);

    if (didDelete) {
      refetchCounts();
    }

    return didDelete;
  };

  return {
    contactId,
    contact: currentContact,
    contactRole,
    contactError,
    fetchContactById,
    refetchSingleContactById,

    contactCounts: countsAPI.contactCounts,
    countsError: countsAPI.countsError,

    documents: documentsAPI.documents,
    notes: documentsAPI.notes,
    loadingDocs: documentsAPI.loadingDocs,
    isSavingDoc: documentsAPI.isSaving,

    loadAllData,
    refetchCounts,
    saveDocument,
    deleteDocument,

    handleSubmitContactForm: formAPI.handleSubmitContactForm,
    isFormLoading: formAPI.isLoading,
  };
};
